/**
 * Runtime bounds for the follow-up scheduler. The lease/stale numbers mirror
 * the debounce contract: every grace period must exceed the worker's own
 * maxDuration so a live worker is never reclaimed.
 */
export const FOLLOW_UP_RUNTIME = {
  /** Lease older than this ⇒ the worker died. Must exceed the worker route's maxDuration (300s). */
  LEASE_STALE_AFTER_S: 360,
  /** Rows per scan — due and stuck are scanned separately, so a tick dispatches up to 2x. */
  BATCH_LIMIT: 50,
  /**
   * A step overdue by more than this is no longer part of the conversation —
   * delivering it late reads worse than not delivering it at all.
   */
  GIVE_UP_AFTER_MIN: 180,
  /** Pre-transport retries (LLM failures only). Past `sending` nothing is retried. */
  MAX_ATTEMPTS: 3,
} as const

/** Written into `content_payload` of the LAST chunk — only it proves the whole reply landed — and of a declined turn's skip marker. */
export const FOLLOW_UP_PAYLOAD_KEY = 'follow_up_id' as const

export type FollowUpStatus =
  | 'pending'
  | 'sending'
  | 'sent'
  | 'skipped'
  | 'cancelled'
  | 'failed'

export type FollowUpSource = 'chain' | 'agent_tool'

/** Why a scheduled follow-up did not reach the customer. */
export const FOLLOW_UP_SKIP_REASONS = {
  AGENT_INACTIVE: 'agent_inactive',
  /** The workspace itself was switched off between arming and firing. */
  TENANT_INACTIVE: 'tenant_inactive',
  REPLY_DISABLED: 'reply_disabled',
  FOLLOW_UPS_DISABLED: 'follow_ups_disabled',
  AI_PAUSED: 'ai_paused',
  /** Contact is on the agent's AI exclusion list — a human owns the conversation. */
  CONTACT_EXCLUDED: 'contact_exclusion',
  CHANNEL_UNAVAILABLE: 'channel_unavailable',
  /** The chain is not configured to run on this conversation's channel. */
  CHANNEL_EXCLUDED: 'channel_excluded',
  WINDOW_EXPIRED: 'messaging_window_expired',
  CUSTOMER_REPLIED: 'customer_replied',
  /** A teammate took the conversation over — independent of auto-pause. */
  OPERATOR_REPLIED: 'operator_replied',
  SESSION_EXPIRED: 'session_expired',
  TOO_LATE: 'overdue_give_up',
  SUPERSEDED: 'superseded',
  /** Smart-check judged a nudge inappropriate. */
  AI_DECLINED: 'ai_declined',
  /** Billing blocked the call — not the model's judgement. */
  AI_ACCESS_BLOCKED: 'ai_access_blocked',
  /** Every provider was down through the attempt budget. */
  LLM_UNAVAILABLE: 'llm_unavailable',
  /** Provider rejected the request outright; retrying cannot help. */
  LLM_TERMINAL: 'llm_terminal',
  GENERATION_FAILED: 'generation_failed',
  /** A `fixed` step whose text is empty — nothing to send. */
  EMPTY_FIXED_TEXT: 'empty_fixed_text',
  /** Transport ran but the customer got nothing. */
  DELIVERY_FAILED: 'delivery_failed',
  /** Transport was entered but the result could not be established. See the at-most-once contract. */
  DELIVERY_UNKNOWN: 'delivery_unknown',
} as const

export type FollowUpSkipReason =
  (typeof FOLLOW_UP_SKIP_REASONS)[keyof typeof FOLLOW_UP_SKIP_REASONS]
